import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { isEqual } from 'lodash';
import { useLocation } from 'wouter';
import JWTService from '../../Services/JWTService';
import {
  createCustomerIdsArray,
  createCustomersArray,
  getCustomers,
  getGdprProfiles,
  getUserData,
  removeUnusedCustomersFromDefaultCustomers,
  setPermissions,
} from '../UserData/UserDataService';
import { parseCookie } from './SsoService';
import LoginService from '../../Services/LoginService';
import { AuthCustomersDTO, AuthUserDTO } from '../UserData/types';
import { gdprSettingsAtom, localeStringsAtom } from '../../store/recoil/AppState';
import {
  customersAtom,
  ICustomer,
  selectedCustomersAtom,
  selectedTempCustomersAtom,
} from '../../store/recoil/customers';
import useAnalytics from '../../hooks/useAnalytics';
import { getDefaultCustomer, getUserId, logOut, setLanguage } from '../../Services/UserService';
import { getTranslation, ITranslation } from '../../Services/LanguageService';
import {
  activeMenuIdAtom,
  activeMenuParentIdAtom,
  carRentalAtom,
  menuItemsAtom,
  urlAtom,
} from '../MainMenu/MainMenuState';
import { getMenuItems } from '../MainMenu/MainMenuService';
import { getVehicleGroups, getVehicles } from '../Navigator/NavigatorService';
import configJs from '../../../config';
import {
  IVehicle,
  IVehicleGroup,
  IVehicles,
  IVehiclesData,
  VehiclesBasicInfo,
} from '../Vehicles/types';
import {
  selectedVehiclesAtom,
  vehicleGroupsAtom,
  vehiclesAtom,
  vehiclesBasicInfoAtom,
  vehiclesOptionsAtom,
} from '../../store/recoil/vehicles';
import {
  IVehicleOnlinePanels,
  onlineMapTemplatesAtom,
  onlinePanelAtom,
} from '../OnlinePanel/OnlinePanelState';
import { IOnlinePanelData } from '../OnlineMapNew/PanelsLayer/types';
import { IWidget } from '../OnlinePanel/Widgets/types';
import { IOption } from '../Forms/CmdField';
import { vehiclesDataAtom } from '../../store/recoil/vehiclesData';
import { usedWidgets } from '../../store/recoil/widgets';
import { localForageStore } from '../../store/localForageStore';
import { SocketConnectionWorker } from '../SocketConnection';
import { EVENT_USER_VEHICLES } from '../SocketConnection/constants';
import {
  getVehiclesCustomerId,
  vehicleArrayToObject,
  vehicleDataArrayToObject,
  vehicleOnlinePanelArrayToObject,
} from '../../Services/VehicleService';

export default function Sso() {
  const cookies = parseCookie(document.cookie);

  if (cookies.authToken && JWTService.getToken() !== cookies.authToken) {
    JWTService.storeToken(cookies.authToken);
    JWTService.storeSocketServerToken(cookies.socketServerToken || '');
    JWTService.storeSocketServerToken(cookies.socketServerToken || '');
  }

  const { setUsername: setUsernameToAnalytics, trackEvent } = useAnalytics();
  const [_, setNavigate] = useLocation();

  const setGdprSettings = useSetRecoilState<Record<string, any>>(gdprSettingsAtom);
  const [selectedCustomers, setSelectedCustomers] = useRecoilState<number[]>(selectedCustomersAtom);
  const setSelectedTempCustomers = useSetRecoilState<number[]>(selectedTempCustomersAtom);
  const setCustomers = useSetRecoilState<ICustomer[]>(customersAtom);
  const setLocaleStrings = useSetRecoilState<ITranslation>(localeStringsAtom);
  const setVehicleGroups = useSetRecoilState<IVehicleGroup[]>(vehicleGroupsAtom);
  const setVehiclesOptions = useSetRecoilState<IOption[]>(vehiclesOptionsAtom);
  const setVehicles = useSetRecoilState<IVehicles>(vehiclesAtom);
  const setVehiclesDataState = useSetRecoilState<IVehiclesData>(vehiclesDataAtom);
  const setUsedWidgets = useSetRecoilState<IWidget[]>(usedWidgets);
  const setOnlineMapTemplates = useSetRecoilState<IOnlinePanelData[]>(onlineMapTemplatesAtom);
  const setOnlinePanel = useSetRecoilState<IVehicleOnlinePanels>(onlinePanelAtom);
  const setVehicleBasicInfo = useSetRecoilState<VehiclesBasicInfo>(vehiclesBasicInfoAtom);
  const setSelectedVehicles = useSetRecoilState<number[]>(selectedVehiclesAtom);

  const [userDataUpdatedAtTimestamp, setUserDataUpdatedAtTimestamp] = useState<number>(0);
  const [gdprProfilesUpdatedAtTimestamp, setGdprProfilesUpdatedAtTimestamp] = useState<number>(0);
  const [customersDataUpdatedAtTimestamp, setCustomersDataUpdatedAtTimestamp] = useState<number>(0);
  const [translationDataUpdatedAtTimestamp, setTranslationDataUpdatedAtTimestamp] =
    useState<number>(0);
  const [menuDataUpdatedAtTimestamp, setMenuDataUpdatedAtTimestamp] = useState<number>(0);
  const [vehicleGroupsDataUpdatedAtTimestamp, setVehicleGroupsDataUpdatedAtTimestamp] =
    useState<number>(0);
  const [vehiclesDataUpdatedAtTimestamp, setVehiclesDataUpdatedAtTimestamp] = useState<number>(0);
  const setCarRental = useSetRecoilState(carRentalAtom);
  const setMenuItems = useSetRecoilState(menuItemsAtom);
  const [activeMenuId, setMenuId] = useRecoilState<number>(activeMenuIdAtom);
  const [activeMenuParentId, setActiveMenuParentId] =
    useRecoilState<number>(activeMenuParentIdAtom);
  const setUrl = useSetRecoilState<string>(urlAtom);

  const { data: userData, dataUpdatedAt: userDataUpdatedAt } = useQuery({
    queryKey: ['userData'],
    queryFn: () => getUserData(),
    enabled: !!cookies,
  });

  const { data: gdprProfilesData, dataUpdatedAt: gdprProfilesUpdatedAt } = useQuery({
    queryKey: ['gdprProfiles'],
    queryFn: () => getGdprProfiles(),
    enabled: !!cookies.authToken && !!userData,
  });

  const { data: customersData, dataUpdatedAt: customersDataUpdatedAt } = useQuery({
    queryKey: ['customers'],
    queryFn: () => getCustomers(),
    enabled: !!cookies.authToken && !!userData && !!gdprProfilesData,
  });

  const { data: translationData, dataUpdatedAt: translationDataUpdatedAt } = useQuery({
    queryKey: ['translations'],
    queryFn: () => getTranslation((userData as AuthUserDTO).language),
    enabled: !!cookies.authToken && !!userData,
  });

  const { data: menuData, dataUpdatedAt: menuDataUpdatedAt } = useQuery({
    queryKey: ['userMenu'],
    queryFn: () => getMenuItems(),
    enabled: !!cookies.authToken && !!userData,
  });

  const { data: vehicleGroupsData, dataUpdatedAt: vehicleGroupsDataUpdatedAt } = useQuery({
    queryKey: ['vehicleGroups'],
    queryFn: () => getVehicleGroups(),
    enabled: !!cookies.authToken && !!userData && !!customersData,
  });

  const { data: vehiclesData, dataUpdatedAt: vehiclesDataUpdatedAt } = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => getVehicles(),
    enabled: !!cookies.authToken && !!userData && !!customersData,
  });

  React.useEffect(() => {
    if (userData && userDataUpdatedAt > userDataUpdatedAtTimestamp) {
      const data = {
        username: (userData as AuthUserDTO).username,
        id: (userData as AuthUserDTO).id,
        driverId: (userData as AuthUserDTO).driverId,
        customerId: (userData as AuthUserDTO).customerId,
        selectedCustomers: (userData as AuthUserDTO).selectedCustomers,
        mapSettings: (userData as AuthUserDTO).mapSettings,
        speedLimits: (userData as AuthUserDTO).speedLimits,
        cookies: (userData as AuthUserDTO).cookies,
      };
      LoginService.setUserData(data);
      setUserDataUpdatedAtTimestamp(userDataUpdatedAt);
      setUsernameToAnalytics((userData as AuthUserDTO).username);
    }
    if (gdprProfilesData && gdprProfilesUpdatedAt > gdprProfilesUpdatedAtTimestamp) {
      setGdprSettings(gdprProfilesData);
      setGdprProfilesUpdatedAtTimestamp(gdprProfilesUpdatedAt);
    }
    if (customersData && customersDataUpdatedAt > customersDataUpdatedAtTimestamp) {
      setPermissions(customersData as AuthCustomersDTO);
      setCustomersDataUpdatedAtTimestamp(customersDataUpdatedAt);
      const customers: ICustomer[] = createCustomersArray(customersData as AuthCustomersDTO);
      if (customers.length > 0) {
        const customerIds: number[] = createCustomerIdsArray(customers);
        const newDefaultCustomers = removeUnusedCustomersFromDefaultCustomers(customerIds);

        const selCustomers = newDefaultCustomers.filter((customerId: number) =>
          customerIds.includes(customerId)
        );
        // Compare selected customers to avaoid unncessary re-renders via router/index.tsx
        if (!isEqual(selCustomers, selectedCustomers)) {
          setSelectedCustomers(selCustomers);
        }
        setSelectedTempCustomers(selCustomers);
        setCustomers(customers);
        setLanguage((userData as AuthUserDTO).language);
        trackEvent(['login', 'sso', 'success']);
      }
    }
    if (translationData && translationDataUpdatedAt > translationDataUpdatedAtTimestamp) {
      setLocaleStrings(translationData as ITranslation);
      setTranslationDataUpdatedAtTimestamp(translationDataUpdatedAt);
      // if (context.updateValue) {
      //   const language = getLanguage();
      //   if (language) {
      //     context.updateValue('language', language);
      //   }
      // }
    }
    if (menuData && menuDataUpdatedAt > menuDataUpdatedAtTimestamp) {
      if (!menuData || menuData?.panel?.items?.length === 0) {
        console.warn('Empty menu items. Logging out. ');
        logOut();
      }

      const menu = menuData.panel || [];
      if (menuData.carRental && menuData.carRental.enabled) {
        setCarRental(menuData.carRental.url);
      }
      if (menu && menu.items && menu.items.length > 0) {
        setMenuItems(menu.items);

        if (
          activeMenuId === 1 &&
          activeMenuParentId === 0 &&
          (menu.items[0].items || menu.items[0].link !== configJs.Login.homeUrl)
        ) {
          if (!menu.items[0].items) {
            setMenuId(menu.items[0].id);
            setActiveMenuParentId(0);
            // setUrl(menu.items[0].link);
            // setNavigate(menu.items[0].link);
          } else {
            setMenuId(menu.items[0].items[Object.keys(menu.items[0].items)[0]].id);
            setActiveMenuParentId(menu.items[0].id);
            // setUrl(menu.items[0].items[Object.keys(menu.items[0].items)[0]].link);
            // setNavigate(menu.items[0].items[Object.keys(menu.items[0].items)[0]].link);
          }
        }
      }
      setMenuDataUpdatedAtTimestamp(menuDataUpdatedAt);
    }
    if (vehicleGroupsData && vehicleGroupsDataUpdatedAt > vehicleGroupsDataUpdatedAtTimestamp) {
      setVehicleGroups(vehicleGroupsData);
      setVehicleGroupsDataUpdatedAtTimestamp(vehicleGroupsDataUpdatedAt);
    }
    if (vehiclesData && vehiclesDataUpdatedAt > vehiclesDataUpdatedAtTimestamp) {
      const vehiclesOptions: IOption[] = Object.values(vehiclesData.vehicles as IVehicle[]).map(
        (i: IVehicle) => {
          return { value: String(i.id), item: `${i.licenceNumber} - ${i.name}` };
        }
      );
      const vehiclesBasicInfo: VehiclesBasicInfo = {};
      vehiclesData.vehicles.forEach((i: IVehicle) => {
        vehiclesBasicInfo[i.id] = {
          id: i.id,
          name: i.name,
          licenceNumber: i.licenceNumber,
          vehicleTypeId: i.vehicleTypeId,
          customerId: i.customerId,
        };
      });
      setVehiclesOptions(vehiclesOptions);
      setVehicles(vehicleArrayToObject(vehiclesData.vehicles));
      setVehiclesDataState(vehicleDataArrayToObject(vehiclesData.vehiclesData));
      setUsedWidgets(vehiclesData.usedWidgets);
      setOnlineMapTemplates(vehiclesData.onlineMapTemplate);
      setOnlinePanel(vehicleOnlinePanelArrayToObject(vehiclesData.onlinePanelData));
      setVehicleBasicInfo(vehiclesBasicInfo);

      const defaultCustomers = getDefaultCustomer();
      if (defaultCustomers.length === 1) {
        const vehiclesByCustomer = getVehiclesCustomerId(
          vehicleArrayToObject(vehiclesData.vehicles),
          [],
          defaultCustomers[0]
        );
        if (vehiclesByCustomer.length === 1) {
          if (vehiclesByCustomer[0]) {
            setSelectedVehicles([vehiclesByCustomer[0].id]);
          }
        }
      }
      // Send user vehicles to SocketServer
      const vehicleIds = vehiclesData.vehicles.map((vehicle: IVehicle) => vehicle.id);
      const data = {
        event: EVENT_USER_VEHICLES,
        payload: vehicleIds,
        userId: getUserId(),
      };
      SocketConnectionWorker.postMessage(data);
      localForageStore.setItem('vehicleIds', vehicleIds);
      setVehiclesDataUpdatedAtTimestamp(vehiclesDataUpdatedAt);
    }
    if (
      userData &&
      menuData &&
      customersData &&
      translationData &&
      vehicleGroupsData &&
      vehiclesData &&
      vehiclesDataUpdatedAtTimestamp > 0
    ) {
      setNavigate(configJs.Login.homeUrl);
    }
  }, [
    activeMenuId,
    activeMenuParentId,
    cookies.authToken,
    cookies.socketServerToken,
    customersData,
    customersDataUpdatedAt,
    customersDataUpdatedAtTimestamp,
    gdprProfilesData,
    gdprProfilesUpdatedAt,
    gdprProfilesUpdatedAtTimestamp,
    menuData,
    menuDataUpdatedAt,
    menuDataUpdatedAtTimestamp,
    selectedCustomers,
    setActiveMenuParentId,
    setCarRental,
    setCustomers,
    setGdprSettings,
    setLocaleStrings,
    setMenuId,
    setMenuItems,
    setNavigate,
    setOnlineMapTemplates,
    setOnlinePanel,
    setSelectedCustomers,
    setSelectedTempCustomers,
    setSelectedVehicles,
    setUrl,
    setUsedWidgets,
    setUsernameToAnalytics,
    setVehicleBasicInfo,
    setVehicleGroups,
    setVehicles,
    setVehiclesDataState,
    setVehiclesOptions,
    trackEvent,
    translationData,
    translationDataUpdatedAt,
    translationDataUpdatedAtTimestamp,
    userData,
    userDataUpdatedAt,
    userDataUpdatedAtTimestamp,
    vehicleGroupsData,
    vehicleGroupsDataUpdatedAt,
    vehicleGroupsDataUpdatedAtTimestamp,
    vehiclesData,
    vehiclesDataUpdatedAt,
    vehiclesDataUpdatedAtTimestamp,
  ]);

  return null;
}
